import React, { useContext, useState } from "react";
import { AuthContext } from "../../../context/authContext";
import { useNavigate } from "react-router-dom";
import { Field, Form } from "react-final-form";
import { Flex } from "../../../components/styled";
import BIButton from "../../../components/common/Button";
import {
  required,
  maxLength,
  composeValidators,
  mustBeNumber,
  validateEmail,
} from "../../../utils/validators";
import BITextField from "../../../components/common/TextField";
import { FromWrapper, StyledDivider } from "../styled";
import { useDispatch } from "react-redux";
import { setSnackbar } from "../../../context/redux/reducers/snackbar";
import {
  IconButton,
  InputAdornment,
  Link,
  Typography,
  Box,
  Grid,
  SvgIcon,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import {
  GppBadOutlined,
  GppGoodOutlined,
  Visibility,
  VisibilityOff,
  Error,
  Close,
  Info,
} from "@mui/icons-material";
import { jwtDecode } from "jwt-decode";
import { useRecaptcha } from "../../../utils/useRecaptcha";
import RecaptchaLoginWrapper from "../../../components/common/Recaptcha/RecaptchaLoginWrapper";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers/DateField";
import { colors } from "../../../theme/colors";

import { ReactComponent as AppstoreLogo } from "../../../img/svg/app-store.svg";
import { ReactComponent as PlayStoreLogo } from "../../../img/svg/google-play.svg";
import { setDialog } from "../../../context/redux/reducers/dialog";
import dayjs from "dayjs";

const InfoModal = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "35px",
          padding: "1rem",
          position: "relative",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          color: colors.gray.textLight,
        }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ padding: "2rem", textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "700",
            fontSize: "32px",
            color: "#2B4C6F",
            marginBottom: "1.5rem",
            lineHeight: "2rem",
          }}
        >
          ¿Por qué pedimos
          <br />
          esta información?
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: "20px",
            lineHeight: "27px",
            color: colors.primary,
            textAlign: "left",
            marginBottom: "2rem",
          }}
        >
          Para brindarte una experiencia segura y personalizada, usamos tu DPI y
          fecha de nacimiento como credenciales de acceso. Estos datos son
          únicos e inalterables, lo que garantiza tu seguridad. Además, nuestra
          plataforma no maneja información sensible, por lo que no hay riesgos
          asociados.
        </Typography>

        <Box
          sx={{
            backgroundColor: colors.yellow.secondary,
            border: "1px solid #FFDB99",
            borderRadius: "15px",
            padding: "1rem",
            display: "flex",
            alignItems: "flex-start",
            gap: "0.5rem",
          }}
        >
          <Info
            sx={{
              color: colors.primary,
              fontSize: "20px",
              marginTop: "1.5rem",
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: "#856404",
              lineHeight: "19px",
              textAlign: "left",
            }}
          >
            Queremos recordarte que esta plataforma está disponible para todo el
            público, sin importar si eres cliente de Banco Industrial o no.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

function LogInColaboratorFormContent() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const context = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { executeAndValidateRecaptcha, cleanupRecaptcha } = useRecaptcha();

  const handleSubmit = async (fromProps) => {
    setLoading(true);

    try {
      const { email, birthDate } = fromProps;

      const response = await fetch(
        `${process.env.REACT_APP_BE_URL}/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email,
            birthDate,
          }),
        },
      );

      const res = await response.json();

      if (res.errorMessage === "Sesión activa") {
        dispatch(
          setSnackbar(true, "error", "Ya tienes una sesión activa.", <Info />),
        );
        return;
      }

      if (res.error === true) {
        const errorMessage = res.errorMessage;
        dispatch(setSnackbar(true, "error", errorMessage, <Info />));
        return;
      }

      if (res.result && typeof res.result === "string" && !res.error) {
        const decodedToken = jwtDecode(res.result);

        dispatch(
          setSnackbar(
            true,
            "success",
            `Bienvenido ${decodedToken.PrimerNombre}`,
            <GppGoodOutlined />,
          ),
        );

        const authResult = { authJWT: res.result };

        context.login(authResult);

        setLoginSuccess(true);

        window.location.reload();
      } else {
        throw new Error("Error de autenticación");
      }
    } catch (err) {
      const errorMessage = err.message || "Error desconocido";

      dispatch(setSnackbar(true, "error", errorMessage, <GppBadOutlined />));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FromWrapper>
      <Typography variant="body1" sx={{ fontWeight: "700", fontSize: "24px" }}>
        Inicia sesión
      </Typography>

      <Box sx={{ padding: { xs: "0 2rem", md: "0 3rem" } }}>
        <Typography
          variant="body1"
          marginTop="2rem"
          sx={{ fontSize: "1rem", color: colors.gray.textLight }}
        >
          Ingresa los datos solicitados.
        </Typography>

        <Form onSubmit={handleSubmit}>
          {(formProps) => (
            <form
              noValidate
              autoComplete="off"
              onSubmit={formProps.handleSubmit}
            >
              <Flex direction="column" fullWidth>
                <Field
                  name="email"
                  component={BITextField}
                  variant="standard"
                  required
                  validate={composeValidators(
                    required,
                    validateEmail(),
                    maxLength("", 50),
                  )}
                  placeholder="Correo eletrónico registrado en Banco Industrial"
                  sx={{
                    marginTop: "2rem",
                    marginBottom: "1rem",
                    "& .MuiInputBase-input::placeholder": {
                      color: colors.primary,
                      fontSize: "14px",
                      opacity: 1,
                    },
                  }}
                />

                <Field name="birthDate" validate={composeValidators(required)}>
                  {({ input }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateField
                        value={input.value ? dayjs(input.value) : null}
                        onChange={(newValue) => {
                          const formattedDate = newValue
                            ? dayjs(newValue).format("YYYY-MM-DD")
                            : null;
                          input.onChange(formattedDate);
                        }}
                        variant="standard"
                        placeholder="Fecha de nacimiento"
                        format="DD/MM/YYYY"
                        sx={{
                          width: "100%",
                          marginTop: "1rem",
                          "& .MuiInputBase-input::placeholder": {
                            color: colors.primary,
                            fontSize: "14px",
                            opacity: 1,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Field>
              </Flex>

              <BIButton
                variant="contained"
                color="primary"
                type="submit"
                loading={loading}
                disabled={loading}
                style={{
                  marginTop: "2rem",
                  width: "80%",
                }}
                id="Btn-iniciosesion-retos"
              >
                Iniciar sesión
              </BIButton>
            </form>
          )}
        </Form>

        <Typography variant="body1" marginTop="2rem" sx={{ fontSize: "1rem" }}>
          Si no eres cliente de Banco Industrial,
          <br />
          crea un usuario aquí:
        </Typography>

        <BIButton
          onClick={() => navigate("/create/user")}
          variant="contained"
          color="secondary"
          type="submit"
          loading={loading}
          disabled={loading}
          style={{
            marginTop: "1rem",
            width: "60%",
          }}
          id="Btn-creaciondeusuario-retos"
        >
          Crear usuario
        </BIButton>

        <Typography variant="body1" marginTop="2rem" sx={{ fontSize: "1rem" }}>
          ¿Por qué te pedimos esta información?
          <br />
          <Link
            onClick={() => setShowInfoModal(true)}
            underline="always"
            sx={{
              color: colors.secondary,
              fontWeight: "700",
              cursor: "pointer",
            }}
          >
            Entérate aquí
          </Link>
        </Typography>
      </Box>

      <Grid container sx={{ marginTop: "3rem" }}>
        <Grid item xs={6} md={6} sx={{ textAlign: "right" }}>
          <Link
            onClick={() => {
              window.location.href =
                "https://apps.apple.com/gt/app/bi-en-l%C3%ADnea/id510761055";
            }}
          >
            <SvgIcon
              component={AppstoreLogo}
              inheritViewBox
              sx={{ width: "150px", color: "#000" }}
            />
          </Link>
        </Grid>
        <Grid item xs={6} md={6} sx={{ textAlign: "left" }}>
          <Link
            onClick={() => {
              window.location.href =
                "https://play.google.com/store/apps/details?id=gt.com.bi.bienlinea&hl=es_GT&gl=US&pli=1";
            }}
          >
            <SvgIcon
              component={PlayStoreLogo}
              inheritViewBox
              sx={{ width: "150px", color: "#000" }}
            />
          </Link>
        </Grid>
      </Grid>

      {/* Modal de información */}
      <InfoModal open={showInfoModal} onClose={() => setShowInfoModal(false)} />
    </FromWrapper>
  );
}

export default function LogInColaboratorForm() {
  return (
    <RecaptchaLoginWrapper>
      <LogInColaboratorFormContent />
    </RecaptchaLoginWrapper>
  );
}
